import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Package, Truck, DollarSign, Edit2, Save, X, Plus, Trash2 } from 'lucide-react';
import './ShippingManager.css';

function ShippingManager() {
  const [shippingMethods, setShippingMethods] = useState([]);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(0);
  const [editingMethod, setEditingMethod] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    carrier: '',
    baseRate: 0,
    estimatedDays: '',
    description: ''
  });

  useEffect(() => {
    loadShippingSettings();
  }, []);

  const loadShippingSettings = async () => {
    try {
      // Load shipping methods
      const shippingSnapshot = await getDocs(collection(db, 'shippingMethods'));
      const methods = shippingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setShippingMethods(methods);

      // Load free shipping threshold
      const settingsDoc = await getDocs(collection(db, 'settings'));
      const threshold = settingsDoc.docs.find(doc => doc.id === 'shipping')?.data()?.freeShippingThreshold || 0;
      setFreeShippingThreshold(threshold);
    } catch (error) {
      console.error('Error loading shipping settings:', error);
    }
  };

  const saveShippingMethod = async () => {
    try {
      const methodData = {
        name: formData.name,
        carrier: formData.carrier,
        baseRate: parseFloat(formData.baseRate),
        estimatedDays: formData.estimatedDays,
        description: formData.description,
        active: true,
        updatedAt: new Date()
      };

      if (editingMethod && editingMethod.id) {
        await updateDoc(doc(db, 'shippingMethods', editingMethod.id), methodData);
      } else {
        await setDoc(doc(db, 'shippingMethods', `method_${Date.now()}`), methodData);
      }

      resetForm();
      loadShippingSettings();
    } catch (error) {
      console.error('Error saving shipping method:', error);
    }
  };

  const updateFreeShippingThreshold = async () => {
    try {
      await setDoc(doc(db, 'settings', 'shipping'), {
        freeShippingThreshold: parseFloat(freeShippingThreshold),
        updatedAt: new Date()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating free shipping threshold:', error);
    }
  };

  const deleteShippingMethod = async (methodId) => {
    try {
      await updateDoc(doc(db, 'shippingMethods', methodId), { active: false });
      loadShippingSettings();
    } catch (error) {
      console.error('Error deleting shipping method:', error);
    }
  };

  const startEditing = (method) => {
    setEditingMethod(method);
    setFormData({
      name: method.name,
      carrier: method.carrier,
      baseRate: method.baseRate,
      estimatedDays: method.estimatedDays,
      description: method.description
    });
  };

  const resetForm = () => {
    setEditingMethod(false);
    setFormData({
      name: '',
      carrier: '',
      baseRate: 0,
      estimatedDays: '',
      description: ''
    });
  };

  const carriers = ['USPS', 'FedEx', 'DHL', 'UPS', 'Other'];

  return (
    <div className="shipping-manager">
      <div className="shipping-header">
        <h2><Truck size={24} /> Shipping Management</h2>
        <p>Configure shipping methods and rates</p>
      </div>

      {/* Free Shipping Threshold */}
      <div className="free-shipping-section">
        <h3><DollarSign size={20} /> Free Shipping Threshold</h3>
        <div className="threshold-input">
          <span>Orders over</span>
          <input
            type="number"
            value={freeShippingThreshold}
            onChange={(e) => setFreeShippingThreshold(e.target.value)}
            placeholder="0.00"
            step="0.01"
          />
          <span>get free shipping</span>
          <button onClick={updateFreeShippingThreshold} className="save-threshold-btn">
            <Save size={16} />
            Update
          </button>
        </div>
      </div>

      {/* Shipping Methods */}
      <div className="shipping-methods-section">
        <div className="section-header">
          <h3><Package size={20} /> Shipping Methods</h3>
          <button 
            className="add-method-btn" 
            onClick={() => setEditingMethod(true)}
          >
            <Plus size={16} />
            Add Method
          </button>
        </div>

        {/* Add/Edit Form */}
        {editingMethod && (
          <div className="shipping-form">
            <h4>{editingMethod.id ? 'Edit Shipping Method' : 'Add Shipping Method'}</h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Method Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Standard Shipping"
                />
              </div>

              <div className="form-group">
                <label>Carrier</label>
                <select
                  value={formData.carrier}
                  onChange={(e) => setFormData({...formData, carrier: e.target.value})}
                >
                  <option value="">Select Carrier</option>
                  {carriers.map(carrier => (
                    <option key={carrier} value={carrier}>{carrier}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Base Rate ($)</label>
                <input
                  type="number"
                  value={formData.baseRate}
                  onChange={(e) => setFormData({...formData, baseRate: e.target.value})}
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>Estimated Delivery</label>
                <input
                  type="text"
                  value={formData.estimatedDays}
                  onChange={(e) => setFormData({...formData, estimatedDays: e.target.value})}
                  placeholder="e.g., 3-5 business days"
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of service"
                />
              </div>
            </div>

            <div className="form-actions">
              <button onClick={resetForm} className="cancel-btn">
                <X size={16} />
                Cancel
              </button>
              <button onClick={saveShippingMethod} className="save-btn">
                <Save size={16} />
                {editingMethod.id ? 'Update' : 'Add'} Method
              </button>
            </div>
          </div>
        )}

        {/* Methods List */}
        <div className="methods-list">
          {shippingMethods.filter(method => method.active).map(method => (
            <div key={method.id} className="method-card">
              <div className="method-info">
                <div className="method-header">
                  <h4>{method.name}</h4>
                  <span className="carrier-badge">{method.carrier}</span>
                </div>
                <p className="method-description">{method.description}</p>
                <div className="method-details">
                  <span className="rate">${method.baseRate.toFixed(2)}</span>
                  <span className="delivery">{method.estimatedDays}</span>
                </div>
              </div>
              <div className="method-actions">
                <button onClick={() => startEditing(method)} className="edit-btn">
                  <Edit2 size={16} />
                </button>
                <button onClick={() => deleteShippingMethod(method.id)} className="delete-btn">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ShippingManager;
